/**
 * API client service for backend communication.
 *
 * Handles all HTTP requests with authentication via Clerk tokens.
 */
import axios, { AxiosInstance, AxiosError } from 'axios'

// Types
export interface Sample {
  sample_id: string
  user_id: string
  notes_text: string
  report_text: string
  created_at: string
  updated_at: string
}

export interface SampleCreate {
  notes_text: string
  report_text: string
}

export interface SampleUpdate {
  notes_text?: string
  report_text?: string
}

export interface SampleListResponse {
  samples: Sample[]
  count: number
}

export interface ReportGenerateRequest {
  notes: string
  model: 'gpt-5' | 'gpt-5-mini' | 'gpt-5-nano'
  reasoning_effort?: 'minimal' | 'low' | 'medium' | 'high'
  use_samples: boolean
}

export interface ReportGenerateResponse {
  report_id: string
  report_text: string
  download_url: string | null
  tokens_used: number | null
  model_used: string
  reasoning_effort: string | null
  samples_used: number
}

export interface DownloadURLResponse {
  download_url: string
  expires_in: number
}

/**
 * API Client class with Clerk authentication.
 */
class APIClient {
  private client: AxiosInstance
  private getToken: (() => Promise<string | null>) | null = null

  constructor() {
    const baseURL = import.meta.env.VITE_API_BASE_URL

    if (!baseURL) {
      throw new Error('VITE_API_BASE_URL is not defined in environment variables')
    }

    this.client = axios.create({
      baseURL,
      timeout: 120000, // 2 minutes for report generation
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      async (config) => {
        if (this.getToken) {
          const token = await this.getToken()
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response) {
          // Server responded with error
          const message = (error.response.data as any)?.detail || error.message
          throw new Error(message)
        } else if (error.request) {
          // Request made but no response
          throw new Error('No response from server. Please check your connection.')
        } else {
          // Error in request setup
          throw new Error(error.message)
        }
      }
    )
  }

  /**
   * Set the token getter function from Clerk.
   *
   * @param getToken - Function that returns Clerk auth token
   */
  setTokenGetter(getToken: () => Promise<string | null>) {
    this.getToken = getToken
  }

  // ========== Sample Endpoints ==========

  /**
   * Create a new training sample.
   */
  async createSample(data: SampleCreate): Promise<Sample> {
    const response = await this.client.post<Sample>('/samples', data)
    return response.data
  }

  /**
   * List all training samples for the authenticated user.
   */
  async listSamples(): Promise<SampleListResponse> {
    const response = await this.client.get<SampleListResponse>('/samples')
    return response.data
  }

  /**
   * Get a specific training sample.
   */
  async getSample(sampleId: string): Promise<Sample> {
    const response = await this.client.get<Sample>(`/samples/${sampleId}`)
    return response.data
  }

  /**
   * Update a training sample.
   */
  async updateSample(sampleId: string, data: SampleUpdate): Promise<Sample> {
    const response = await this.client.put<Sample>(`/samples/${sampleId}`, data)
    return response.data
  }

  /**
   * Delete a training sample.
   */
  async deleteSample(sampleId: string): Promise<void> {
    await this.client.delete(`/samples/${sampleId}`)
  }

  // ========== Generate Endpoint ==========

  /**
   * Generate a report from meeting notes.
   */
  async generateReport(data: ReportGenerateRequest): Promise<ReportGenerateResponse> {
    const response = await this.client.post<ReportGenerateResponse>('/generate', data)
    return response.data
  }

  // ========== Transcribe Endpoint ==========

  /**
   * Transcribe text from a handwritten notes image.
   */
  async transcribeImage(file: File): Promise<{ transcribed_text: string }> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.client.post<{ transcribed_text: string }>(
      '/transcribe',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  // ========== Download Endpoint ==========

  /**
   * Get download URL for a report's Word document.
   */
  async getDownloadURL(reportId: string): Promise<DownloadURLResponse> {
    const response = await this.client.get<DownloadURLResponse>(`/download/${reportId}`)
    return response.data
  }
}

// Export singleton instance
export const apiClient = new APIClient()
